import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { REPORT_ACTION_LABELS, REPORT_STATUS_LABELS, SAFETY_COPY, type ReportStatus } from '@saesak/shared';
import { Button, Card, Chip, SectionTitle } from '@/components/ui';
import { ApiError, apiData } from '@/lib/api';
import { formatRelative } from '@/lib/format';

export interface SafetyReport {
  id: string;
  targetType: string;
  targetLabel: string;
  targetId: string;
  reasonLabel: string;
  detail: string | null;
  excerpt: string | null;
  status: ReportStatus;
  action: string | null;
  reporterName: string;
  reportedUserName: string | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  createdAt: string;
  facilityName?: string;
}

/**
 * 신고 접수함 — 원장 앱 관리 화면 (§5.30).
 * 확인 대기 건을 위에 두고, 내용 숨김·당사자 안내·조치 없음·문제 없음으로 닫는다.
 */
export function ReportsSection() {
  const queryClient = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data } = useQuery({
    queryKey: ['safety', 'reports', showAll ? 'all' : 'open'],
    queryFn: () => apiData<SafetyReport[]>(`/safety/reports${showAll ? '' : '?status=OPEN'}`),
    placeholderData: keepPreviousData,
  });

  const review = useMutation({
    mutationFn: (input: { id: string; status: 'RESOLVED' | 'DISMISSED'; action?: string }) =>
      apiData(`/safety/reports/${input.id}`, {
        method: 'PATCH',
        body: { status: input.status, action: input.action },
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['safety', 'reports'] });
      // 숨긴 내용이 화면에서 바로 빠지게
      void queryClient.invalidateQueries({ queryKey: ['messages'] });
      void queryClient.invalidateQueries({ queryKey: ['daily-notes'] });
      void queryClient.invalidateQueries({ queryKey: ['albums'] });
      void queryClient.invalidateQueries({ queryKey: ['home-messages'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : '처리하지 못했어요.'),
  });

  const items = data ?? [];

  return (
    <section>
      <SectionTitle
        action={
          <button
            type="button"
            className="text-sm font-semibold text-primary-strong"
            onClick={() => setShowAll((prev) => !prev)}
          >
            {showAll ? '대기 중만' : '전체 보기'}
          </button>
        }
      >
        {SAFETY_COPY.reportsTitle}
      </SectionTitle>

      {items.length === 0 ? (
        <Card>
          <p className="text-sm text-ink-sub">{SAFETY_COPY.reportsEmpty}</p>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="px-1 text-sm text-ink-sub">{SAFETY_COPY.reportsHint}</p>
          {items.map((item) => (
            <Card key={item.id}>
              <div className="flex flex-wrap items-center gap-1.5">
                <Chip tone="primary">{item.targetLabel}</Chip>
                <Chip tone="accent">{item.reasonLabel}</Chip>
                <Chip tone={item.status === 'OPEN' ? 'secondary' : 'neutral'}>
                  {REPORT_STATUS_LABELS[item.status] ?? item.status}
                </Chip>
                <span className="ml-auto text-sm text-ink-sub">{formatRelative(item.createdAt)}</span>
              </div>
              {item.excerpt && (
                <p className="mt-2 whitespace-pre-wrap rounded-sm bg-bg px-3 py-2 text-base">{item.excerpt}</p>
              )}
              <p className="mt-2 text-sm text-ink-sub">
                신고 {item.reporterName}
                {item.reportedUserName ? ` · 대상 ${item.reportedUserName}` : ''}
                {item.facilityName ? ` · ${item.facilityName}` : ''}
              </p>
              {item.detail && <p className="mt-1 whitespace-pre-wrap text-sm">{item.detail}</p>}
              {item.status !== 'OPEN' && (
                <p className="mt-1 text-sm text-ink-sub">
                  {item.action ? (REPORT_ACTION_LABELS[item.action as keyof typeof REPORT_ACTION_LABELS] ?? item.action) : ''}
                  {item.reviewedByName ? ` · ${item.reviewedByName}` : ''}
                  {item.reviewedAt ? ` · ${formatRelative(item.reviewedAt)}` : ''}
                </p>
              )}
              {item.status === 'OPEN' && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {item.targetType !== 'USER' && (
                    <Button
                      size="sm"
                      loading={review.isPending}
                      onClick={() => {
                        if (window.confirm('이 내용을 숨길까요? 당사자를 포함한 모두에게 보이지 않게 돼요.')) {
                          review.mutate({ id: item.id, status: 'RESOLVED', action: 'HIDDEN' });
                        }
                      }}
                    >
                      내용 숨기기
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    loading={review.isPending}
                    onClick={() => review.mutate({ id: item.id, status: 'RESOLVED', action: 'WARNED' })}
                  >
                    당사자에게 안내함
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    loading={review.isPending}
                    onClick={() => review.mutate({ id: item.id, status: 'DISMISSED' })}
                  >
                    문제 없음
                  </Button>
                </div>
              )}
            </Card>
          ))}
          {error && (
            <p role="alert" className="rounded-sm bg-notice/10 px-3 py-2 text-sm text-notice">
              {error}
            </p>
          )}
        </div>
      )}
    </section>
  );
}
